import { Pill, SurfacePagePanel } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { MeterBar, TrendChart } from '@/versions/v1/modules/_viz';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import type { AiBandPanel, PerformanceViewModel } from './Performance.transform';
import { MineNudgeRows } from './MineNudgeRow';

function ClausePills({
  clauses,
  wrap = false,
}: {
  clauses: readonly { text: string; confirmed: boolean; provenance: string; pillText?: string }[];
  /** Lines wrap in full (GM landing, comp `.ev`): pill and text flow together, the code sits right. */
  wrap?: boolean;
}) {
  if (clauses.length === 0) return null;
  if (wrap) {
    return (
      <div className="mt-2 space-y-1.5" data-clause-rows="wrap">
        {clauses.map((clause, index) => (
          <div
            key={`${clause.provenance}-${index}`}
            className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2"
          >
            <p className="min-w-0 text-sm leading-snug text-zinc-900 dark:text-zinc-100">
              <Pill
                tone={clause.confirmed ? 'success' : 'warning'}
                className="mr-1.5 px-1.5 py-0 align-[1px] text-[10px] normal-case tracking-normal"
              >
                {clause.pillText ?? (clause.confirmed ? 'Confirmed' : 'Unconfirmed')}
              </Pill>
              {clause.text}
            </p>
            <ProvenanceChip text={clause.provenance} compact />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="mt-2 space-y-1.5">
      {clauses.map((clause, index) => (
        <div
          key={`${clause.provenance}-${index}`}
          className="flex min-w-0 items-center gap-2"
        >
          <Pill
            tone={clause.confirmed ? 'success' : 'warning'}
            className="shrink-0 px-1.5 py-0 text-[10px] normal-case tracking-normal"
          >
            {clause.pillText ?? (clause.confirmed ? 'Confirmed' : 'Unconfirmed')}
          </Pill>
          <p
            className="min-w-0 flex-1 truncate text-sm text-zinc-900 dark:text-zinc-100"
            title={clause.text}
          >
            {clause.text}
          </p>
          <ProvenanceChip text={clause.provenance} compact />
        </div>
      ))}
    </div>
  );
}

export function AiBandPanelBody({ panel, wrapClauses = false }: { panel: AiBandPanel; wrapClauses?: boolean }) {
  return (
    <div className="space-y-3">
      {panel.body ? <p className="text-sm text-zinc-900 dark:text-zinc-100">{panel.body}</p> : null}
      {panel.forecastMeters?.map((row) => (
        <div key={row.label} className="space-y-1 border-t border-[var(--hairline)] pt-3 first:border-t-0 first:pt-0">
          <MeterBar
            label={row.label}
            value={row.value}
            max={100}
            marker={{ value: 100, label: 'Plan 100%' }}
            tone={row.tone}
            valueLabel={`${row.value.toFixed(1)}%`}
            markerCaption={false}
          />
          <p className="text-[10px] text-muted">{row.caption}</p>
        </div>
      ))}
      {panel.forecastLine ? (
        <p className="border-t border-[var(--hairline)] pt-3 text-sm text-zinc-900 dark:text-zinc-100">
          {panel.forecastLine}
        </p>
      ) : null}
      {panel.forecastGapLine ? (
        <p className="border-t border-[var(--hairline)] pt-2 text-xs font-medium uppercase tracking-wide text-muted">
          {panel.forecastGapLine}
        </p>
      ) : null}
      {panel.causeLines?.map((line) => (
        <p
          key={line.label}
          className="border-t border-[var(--hairline)] pt-2 text-sm text-zinc-900 dark:text-zinc-100"
        >
          <span className="font-medium">{line.label}</span>
          <span className="tabular"> · {line.td} t/d</span>
        </p>
      ))}
      {panel.mineNudgeRows && panel.mineNudgeRows.length > 0 ? (
        <div className="border-t border-[var(--hairline)] pt-2">
          <MineNudgeRows rows={panel.mineNudgeRows} />
        </div>
      ) : null}
      {panel.rows?.map((row) => (
        <div key={row.label} className="border-t border-[var(--hairline)] pt-3 first:border-t-0 first:pt-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              {row.pillText ? (
                <Pill
                  tone={row.confirmed ? 'success' : 'warning'}
                  className="shrink-0 px-1.5 py-0 text-[10px] normal-case tracking-normal"
                >
                  {row.pillText}
                </Pill>
              ) : (
                <p className="text-xs font-medium uppercase tracking-wide text-muted">{row.label}</p>
              )}
            </div>
            <ProvenanceChip text={row.provenance} compact />
          </div>
          <p className="mt-1 text-sm">{row.content}</p>
        </div>
      ))}
      {panel.threadCards?.map((card) => (
        <div
          key={card.title}
          className="rounded-lg border border-[var(--hairline)] px-3 py-2"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium">{card.title}</p>
            <Pill tone="default">{card.confirmedPill}</Pill>
          </div>
          <ClausePills clauses={card.clauses} wrap={wrapClauses} />
        </div>
      ))}
      {panel.contextCards?.map((card) => (
        <div
          key={card.title}
          className={cn(
            'rounded-lg border border-[var(--hairline)] border-l-[3px] px-3 py-2',
            card.borderTone === 'bad'
              ? 'border-l-[var(--state-blocked)]'
              : card.borderTone === 'warn'
                ? 'border-l-[var(--state-warning)]'
                : 'border-l-[var(--state-online)]',
          )}
        >
          <p className="text-sm font-medium">{card.title}</p>
          <p className="mt-1 text-sm text-muted">{card.body}</p>
          <ClausePills clauses={card.clauses} wrap={wrapClauses} />
        </div>
      ))}
      {panel.checklist && panel.checklist.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted">
            Worth investigating or sampling this shift
          </p>
          <ul className="space-y-2 text-sm">
            {panel.checklist.map((item) => (
              <li key={item.text} className="flex justify-between gap-3 border-b border-[var(--hairline)] py-2 last:border-0">
                <span>{item.text}</span>
                <span className="shrink-0 text-xs text-muted">{item.tail}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {panel.valueRows?.map((row) => (
        <p key={row.text} className="text-sm">
          <span className="font-mono text-muted">{row.sign}</span> {row.text}
        </p>
      ))}
      {panel.trendCategories && panel.trendPoints ? (
        <TrendChart
          kind="line"
          categories={panel.trendCategories}
          series={[
            {
              name: 'Zero-touch share',
              points: [...panel.trendPoints],
              tone: 'ok',
              area: true,
            },
          ]}
          height={48}
          yDomain={panel.trendYDomain}
          labelLastPoint={panel.labelLastPoint}
        />
      ) : null}
      {panel.footnote ? <p className="text-xs text-muted">{panel.footnote}</p> : null}
    </div>
  );
}

function panelColSpan(
  panel: AiBandPanel,
  layout: NonNullable<PerformanceViewModel['aiBand']>['layout'],
): string {
  if (layout === 'gm-full') {
    if (panel.id === 'ai-briefing') return 'col-span-12';
    return 'col-span-12 lg:col-span-4';
  }
  if (layout === 'met-split') {
    return 'col-span-12';
  }
  return 'col-span-12 lg:col-span-6';
}

export function AiBand({
  band,
  panelMaxHeight,
}: {
  band: NonNullable<PerformanceViewModel['aiBand']>;
  panelMaxHeight?: string;
}) {
  return (
    <Commentable name="AiBand">
      <div className="border-t border-[var(--hairline)] pt-4">
        <div className="grid grid-cols-12 gap-4">
          {band.panels.map((panel) => (
            <div
              key={panel.id}
              className={panelColSpan(panel, band.layout)}
            >
              <SurfacePagePanel
                title={panel.title}
                subtitle={panel.subtitle}
                actions={
                  panel.provenance ? (
                    <ProvenanceChip
                      text={panel.provenanceChip ?? panel.provenance}
                      title={panel.provenanceChip ? panel.provenance : undefined}
                      compact
                    />
                  ) : undefined
                }
                maxHeight={panelMaxHeight}
              >
                <AiBandPanelBody panel={panel} />
              </SurfacePagePanel>
            </div>
          ))}
        </div>
      </div>
    </Commentable>
  );
}
